import React, { useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Calculator, Leaf, IndianRupee, Zap, TrendingUp, Clock } from "lucide-react";
import { calculateSolar, submitLead } from "../lib/api";
import { toast } from "sonner";

const CITIES = ["Delhi", "Gurgaon", "Noida", "Faridabad", "Ghaziabad", "Jaipur", "Lucknow", "Mumbai", "Pune", "Bangalore", "Chennai", "Hyderabad", "Kolkata", "Ahmedabad"];

function StatCard({ icon: Icon, label, value, accent = "emerald" }) {
  const colors = {
    emerald: "bg-blue-50 text-blue-800 border-blue-200",
    slate: "bg-slate-50 text-slate-800 border-slate-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[accent]}`}>
      <div className="flex items-center gap-2 mb-1 text-xs font-semibold uppercase tracking-wide opacity-80">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="font-heading text-xl sm:text-2xl font-bold stat-number">{value}</div>
    </div>
  );
}

export default function SolarCalculator({ compact = false }) {
  const [bill, setBill] = useState(5000);
  const [city, setCity] = useState("Delhi");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", phone: "" });
  const [leadSubmitting, setLeadSubmitting] = useState(false);

  async function handleCalculate() {
    setLoading(true);
    try {
      const data = await calculateSolar({ monthly_bill: bill, city });
      setResult(data);
    } catch (e) {
      toast.error("Calculation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleLead(e) {
    e.preventDefault();
    if (!leadForm.name || !leadForm.phone) {
      toast.error("Please enter name and phone");
      return;
    }
    setLeadSubmitting(true);
    try {
      await submitLead({
        ...leadForm,
        city,
        source: "calculator",
        monthly_bill: bill,
        estimated_system_kw: result?.recommended_kw,
        message: `Auto-generated quote. System: ${result?.recommended_kw} kW, Cost: ₹${result?.net_cost_inr}`,
      });
      toast.success("We'll call you within 24 hours with your detailed quote.");
      setLeadForm({ name: "", phone: "" });
    } catch (e) {
      toast.error("Something went wrong");
    } finally {
      setLeadSubmitting(false);
    }
  }

  const fmtINR = (v) => "₹" + Number(v || 0).toLocaleString("en-IN");

  return (
    <div className={`grid gap-6 ${compact ? "grid-cols-1" : "lg:grid-cols-5"}`} data-testid="solar-calculator">
      {/* Inputs */}
      <div className={`bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm ${compact ? "" : "lg:col-span-2"}`}>
        <div className="flex items-center gap-2 mb-5">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <Calculator className="w-5 h-5 text-blue-700" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-700 font-bold">Solar Calculator</div>
            <div className="font-heading text-lg font-semibold text-slate-900">Estimate your savings</div>
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-2 block">Monthly Electricity Bill (₹)</Label>
            <div className="flex items-center gap-3">
              <IndianRupee className="w-5 h-5 text-slate-400 shrink-0" />
              <Input
                type="number"
                value={bill}
                onChange={(e) => setBill(Number(e.target.value) || 0)}
                className="flex-1 text-lg font-semibold"
                data-testid="calc-bill-input"
              />
            </div>
            <Slider value={[bill]} min={500} max={100000} step={500} onValueChange={(v) => setBill(v[0])} className="mt-4" data-testid="calc-bill-slider" />
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>₹500</span><span>₹1,00,000</span>
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium text-slate-700 mb-2 block">City</Label>
            <Input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Enter your city"
            className="w-full"
          />

          </div>

          <Button onClick={handleCalculate} disabled={loading} className="w-full bg-yellow-400 hover:bg-yellow-500 text-slate-900 text-base py-6" data-testid="calc-submit-btn">
            {loading ? "Calculating..." : "Calculate My Savings"}
          </Button>
        </div>
      </div>

      {/* Results */}
      <div className={`rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 p-6 sm:p-8 text-white relative overflow-hidden ${compact ? "" : "lg:col-span-3"}`}>
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-600/20 rounded-full blur-3xl" />
        <div className="relative">
          <div className="text-xs uppercase tracking-widest text-blue-300 font-bold mb-2">Your Solar Plan</div>
          <h3 className="font-heading text-2xl sm:text-3xl font-bold mb-5">
            {result ? `${result.recommended_kw} kW solar system` : "Your custom quote appears here"}
          </h3>

          {result ? (
            <>
              <div className="grid grid-cols-2 gap-3 mb-5">
                <StatCard icon={Zap} label="System Size" value={`${result.recommended_kw} kW`} accent="slate" />
                <StatCard icon={IndianRupee} label="Net Cost (after subsidy)" value={fmtINR(result.net_cost_inr)} accent="emerald" />
                <StatCard icon={TrendingUp} label="Monthly Savings" value={fmtINR(result.monthly_savings_inr)} accent="emerald" />
                <StatCard icon={Clock} label="Payback" value={`${result.payback_years} yrs`} accent="amber" />
              </div>
              <div className="text-sm text-slate-300 space-y-1 mb-5">
                <div className="flex justify-between"><span>Roof area needed</span><span className="font-semibold text-white">{result.roof_area_sqft} sq ft</span></div>
                <div className="flex justify-between"><span>Govt subsidy (up to)</span><span className="font-semibold text-blue-300">{fmtINR(result.subsidy_inr)}</span></div>
                <div className="flex justify-between"><span>25-year lifetime savings</span><span className="font-semibold text-blue-300">{fmtINR(result.lifetime_savings_inr)}</span></div>
                <div className="flex justify-between"><span><Leaf className="w-3 h-3 inline mr-1" /> CO₂ offset / year</span><span className="font-semibold text-blue-300">{result.co2_offset_tons_year} tons</span></div>
              </div>

              {!compact && (
                <form onSubmit={handleLead} className="bg-white/10 backdrop-blur rounded-xl p-4 space-y-2 border border-white/20" data-testid="calc-lead-form">
                  <div className="text-sm font-semibold">Get a detailed quote by phone</div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Name" value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} className="bg-white text-slate-900" data-testid="calc-lead-name" />
                    <Input placeholder="Phone" value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} className="bg-white text-slate-900" data-testid="calc-lead-phone" />
                  </div>
                  <Button type="submit" disabled={leadSubmitting} className="w-full bg-yellow-400 hover:bg-yellow-500 text-slate-900" data-testid="calc-lead-submit">
                    {leadSubmitting ? "Sending..." : "Send me the detailed quote"}
                  </Button>
                </form>
              )}
            </>
          ) : (
            <div className="text-sm text-slate-300 leading-relaxed">
              Enter your monthly electricity bill on the left and we'll calculate:
              <ul className="mt-3 space-y-1.5 text-slate-300">
                <li>• Recommended system size (kW)</li>
                <li>• Project cost after government subsidy</li>
                <li>• Monthly and lifetime savings</li>
                <li>• Payback period & CO₂ offset</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
